/**
 * The marks a diff leaves inside a line, and how to put them on.
 *
 * Their own module because both halves of the diff need them: the line walk
 * in `rules-diff`, and the table pass in `rules-tables`. One importing the
 * other for a constant would make the two import each other.
 */
/**
 * Sentinels wrapping the words that actually changed inside a line. They survive
 * markdown parsing as ordinary text, so the renderer can mark the words after
 * the document has been parsed rather than injecting HTML into it. Both are
 * zero-width in every font, so a leak is invisible rather than wrong.
 */
export const MARK_OPEN = "\u2062";
export const MARK_CLOSE = "\u2063";

/**
 * The same, for words that LEFT. A block's own colour says which it is almost
 * everywhere, but a changed table row has to show both at once: the table has
 * to stay one table, so there is no second row to colour.
 */
export const DROP_OPEN = "\u2064";
export const DROP_CLOSE = "\u206a";

// Markdown delimiters have to stay outside the mark.
//
// The sentinels are invisible characters, but CommonMark still counts them when
// it decides whether `**` opens emphasis. Wrapping a whole token produced
// `\u2062**30%**\u2063`, where the `**` no longer flanks a word, so the asterisks
// rendered literally and a rate change read as `**30%**` on screen. Peeling the
// delimiters off and marking only what sits between them keeps the bold and
// marks the word inside it.
const DELIMITERS = /^([*_`~]*)([\s\S]*?)([*_`~]*)$/;

export const wrapMark = (text: string) => {
  const match = DELIMITERS.exec(text);
  if (!match) return MARK_OPEN + text + MARK_CLOSE;

  const [, open, body, close] = match;
  if (!body) return text;

  return open + MARK_OPEN + body + MARK_CLOSE + close;
};

/** The same, for a value the proposal takes away. */
export const wrapDrop = (text: string) => {
  const match = DELIMITERS.exec(text);
  if (!match) return DROP_OPEN + text + DROP_CLOSE;
  const [, open, body, close] = match;
  if (!body) return text;
  return open + DROP_OPEN + body + DROP_CLOSE + close;
};

/** A line belonging to a markdown table, including its header rule. */
export const TABLE_ROW = /^\s*\|/;
